import { useState, useEffect, useRef } from 'react';
import { Box, Typography, Stack, LinearProgress, Button } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
  CONTENT_BLOCK_HEIGHT,
  externalFlowContentColumnSx,
  externalFlowSecondaryTextButtonSx,
  externalFlowGlassCardLayoutSx,
  externalFlowGlassSurfaceSx,
  externalFlowIconGlassWellSx,
  externalFlowInCardBrandingSx,
  externalFlowLinearProgressTrackSx,
  externalFlowLineClamp3Sx,
  FLOW_MB_AFTER_ICON,
  FLOW_ICON_TO_TITLE_EXTRA_PX,
  FLOW_MB_AFTER_PROGRESS,
  FLOW_MB_AFTER_SUBTITLE,
  FLOW_MB_AFTER_TITLE,
  LABEL_PRIMARY,
  LABEL_SECONDARY,
  LABEL_TERTIARY,
  LETTER_SPACING,
  SUBTITLE_FONT_SIZE,
  TITLE_FONT_SIZE,
  UPLOAD_FLOW_PAGE_BG,
} from './uploadCardLayout';

const CYCLING_MESSAGES = [
  { delay: 0,     text: 'Analyzing...' },
  { delay: 1500,  text: 'Identifying rooms...' },
  { delay: 3000,  text: 'Reading dimensions...' },
  { delay: 4500,  text: 'Placing furniture to scale...' },
  { delay: 6000,  text: 'Trying different layouts...' },
  { delay: 8000,  text: 'Building your interactive layout...' },
  { delay: 11000, text: 'Shuffle until you like what you see...' },
  { delay: 16000, text: 'Complex plans take a moment...' },
  { delay: 22000, text: 'Still working...' },
  { delay: 30000, text: 'Nearly there...' },
];

const ProcessingCard = ({ statusMessage, elapsedTime, onCancel }) => {
  const [msgIndex, setMsgIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const timersRef = useRef([]);

  useEffect(() => {
    timersRef.current = CYCLING_MESSAGES.slice(1).map((msg, i) =>
      setTimeout(() => {
        setVisible(false);
        setTimeout(() => {
          setMsgIndex(i + 1);
          setVisible(true);
        }, 400);
      }, msg.delay)
    );
    return () => timersRef.current.forEach(clearTimeout);
  }, []);

  const formatElapsedTime = (ms) => {
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) {
      return `${seconds}s`;
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 0,
        flexShrink: 0,
        bgcolor: UPLOAD_FLOW_PAGE_BG,
        width: '100%',
      }}
    >
      <Box
        sx={{
          ...externalFlowContentColumnSx,
          minHeight: CONTENT_BLOCK_HEIGHT,
          maxHeight: CONTENT_BLOCK_HEIGHT,
        }}
      >
        <Box
          sx={{
            ...externalFlowGlassCardLayoutSx,
            ...externalFlowGlassSurfaceSx,
          }}
        >
        <Stack spacing={0} alignItems="center" sx={{ width: '100%', height: '100%' }}>
          <Box sx={{ flex: 1, minHeight: 0 }} />

          {/* Sparkle / magic — squircle glass well */}
          <Box
            sx={(theme) => ({
              ...externalFlowIconGlassWellSx({ size: 48, borderRadius: '17px' }),
              position: 'relative',
              mb: `calc(${theme.spacing(FLOW_MB_AFTER_ICON)} + ${FLOW_ICON_TO_TITLE_EXTRA_PX}px)`,
            })}
          >
            <AutoAwesomeIcon
              sx={{
                fontSize: 20,
                color: LABEL_SECONDARY,
                animation: 'sparkle 1.5s ease-in-out infinite',
                '@keyframes sparkle': {
                  '0%, 100%': {
                    opacity: 1,
                    transform: 'scale(1) rotate(0deg)',
                  },
                  '50%': {
                    opacity: 0.72,
                    transform: 'scale(1.08) rotate(180deg)',
                  },
                },
              }}
            />
            <AutoAwesomeIcon
              sx={{
                fontSize: 11,
                color: LABEL_TERTIARY,
                position: 'absolute',
                top: 8,
                right: 8,
                animation: 'sparkle-secondary 2s ease-in-out infinite',
                '@keyframes sparkle-secondary': {
                  '0%, 100%': {
                    opacity: 0.5,
                    transform: 'scale(0.85) rotate(0deg)',
                  },
                  '50%': {
                    opacity: 1,
                    transform: 'scale(1) rotate(-180deg)',
                  },
                },
              }}
            />
          </Box>

          {/* Title */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              color: LABEL_PRIMARY,
              fontSize: TITLE_FONT_SIZE,
              letterSpacing: LETTER_SPACING,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
              px: 0.5,
              mb: FLOW_MB_AFTER_TITLE,
            }}
          >
            One moment...
          </Typography>

          {/* Subtitle — cycling status messages */}
          <Typography
            variant="body1"
            sx={{
              color: visible ? LABEL_SECONDARY : 'rgba(0,0,0,0.15)',
              fontSize: SUBTITLE_FONT_SIZE,
              lineHeight: 1.4,
              mb: FLOW_MB_AFTER_SUBTITLE,
              textAlign: 'center',
              opacity: visible ? 1 : 0.3,
              transition: 'color 400ms ease-in-out, opacity 400ms ease-in-out',
              ...externalFlowLineClamp3Sx,
            }}
          >
            {CYCLING_MESSAGES[msgIndex].text}
          </Typography>

          {/* Progress Bar */}
          <Box
            sx={{
              width: '100%',
              maxWidth: 240,
              mb: elapsedTime || onCancel ? FLOW_MB_AFTER_PROGRESS : 0,
            }}
          >
            <LinearProgress sx={externalFlowLinearProgressTrackSx} />
          </Box>

          {/* Elapsed Time */}
          {elapsedTime && (
            <Typography
              variant="body2"
              sx={{
                color: LABEL_TERTIARY,
                fontSize: '13px',
                lineHeight: 1.45,
                fontWeight: 500,
                letterSpacing: LETTER_SPACING,
                mb: onCancel ? 1 : 0,
              }}
            >
              Elapsed time: {formatElapsedTime(elapsedTime)}
            </Typography>
          )}

          {/* Cancel Button */}
          {onCancel && (
            <Button
              onClick={onCancel}
              sx={{
                ...externalFlowSecondaryTextButtonSx,
                mt: elapsedTime ? 0 : 0.75,
              }}
            >
              Cancel
            </Button>
          )}

          <Box sx={{ flex: 1, minHeight: 0 }} />

          {/* In-card branding */}
          <Typography sx={externalFlowInCardBrandingSx}>
            Powered by MagicFurnish
          </Typography>
        </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default ProcessingCard;
